// Node-type registry passed to ReactFlow. Mirrors the pattern at
// components/workflows/nodes/index.ts — one keyed export per type so
// ReactFlow can dispatch by `node.type`.
//
// v0.4: dispatched kinds (turn_marker / llm_round / tool_call / peer)
// go through `withFocalDispatch(Compact, Rich)` so the focal turn
// flowers into a rich card. Until focalTurnId is non-null (Task 11
// wires it), the HOF always picks the compact variant → v0.3 visual
// behavior preserved with zero feature flag.
//
// Non-dispatched structural kinds (fan_collapsed_chip, phase_stack_
// group, collapsed_phase_stack) have no rich variant — they're
// layout primitives, not agent cards (architect A2').

import { byKind } from './byKind';
import { withFocalDispatch } from './withFocalDispatch';
import TurnMarkerNodeCompactImpl from './TurnMarkerNodeCompact';
import LlmRoundNodeCompactImpl from './LlmRoundNodeCompact';
import ToolCallDagNodeCompactImpl from './ToolCallDagNodeCompact';
import PeerNodeCompactImpl from './PeerNodeCompact';
import { TurnMarkerNodeRich } from './rich/TurnMarkerNodeRich';
import { LlmRoundNodeRich } from './rich/LlmRoundNodeRich';
import { ToolCallDagNodeRich } from './rich/ToolCallDagNodeRich';
import { PeerNodeRich } from './rich/PeerNodeRich';
import FanCollapsedChipImpl from './FanCollapsedChip';
import PhaseStackGroupNodeImpl from './PhaseStackGroupNode';
import CollapsedPhaseStackNodeImpl from './CollapsedPhaseStackNode';
import TurnSeparatorNodeImpl from './TurnSeparatorNode';
// Trailmap L1 (plan Task 12) — artifact tiles inline + Orphan Tray (DC3).
import ArtifactTileNodeImpl from './ArtifactTileNode';
import OrphanTrayNodeImpl from './OrphanTrayNode';
// v0.2 Council Trail peer-spine refactor — 4 new node kinds (compact-only).
import SpineRootMarkerNodeImpl from './SpineRootMarkerNode';
import PeerResponseNodeImpl from './PeerResponseNode';
import SpineFanChipNodeImpl from './SpineFanChipNode';
import CycleErrorNodeImpl from './CycleErrorNode';
// v0.2.x — chrome chip at the spine root of any trail.
import TrailHeadMarkerNodeImpl from './TrailHeadMarkerNode';
import SquadMergeNodeImpl from './SquadMergeNode';
import MergeNodeImpl from './MergeNode';
import ClusterAabbNodeImpl from './ClusterAabbNode';
// Living Blueprint SP1 — summoned blueprint flip card.
import BlueprintNode from './BlueprintNode';
import SpineLaneStemNodeImpl from './SpineLaneStemNode';
import SpineChatPanelNodeImpl from './SpineChatPanelNode';

export const chatDagNodeTypes = {
  turn_marker: byKind(
    'turn_marker',
    withFocalDispatch(
      TurnMarkerNodeCompactImpl as any,
      TurnMarkerNodeRich,
    ) as any,
  ),
  llm_round: byKind(
    'llm_round',
    withFocalDispatch(LlmRoundNodeCompactImpl as any, LlmRoundNodeRich) as any,
  ),
  tool_call: byKind(
    'tool_call',
    withFocalDispatch(
      ToolCallDagNodeCompactImpl as any,
      ToolCallDagNodeRich,
    ) as any,
  ),
  peer: byKind(
    'peer',
    withFocalDispatch(PeerNodeCompactImpl as any, PeerNodeRich) as any,
  ),
  fan_collapsed_chip: byKind('fan_collapsed_chip', FanCollapsedChipImpl as any),
  phase_stack_group: byKind('phase_stack_group', PhaseStackGroupNodeImpl as any),
  collapsed_phase_stack: byKind(
    'collapsed_phase_stack',
    CollapsedPhaseStackNodeImpl as any,
  ),
  turn_separator: byKind('turn_separator', TurnSeparatorNodeImpl as any),
  // Trailmap L1 (plan Task 12) — structural nodes, no focal dispatch.
  artifact_tile: byKind('artifact_tile', ArtifactTileNodeImpl as any),
  orphan_tray: byKind('orphan_tray', OrphanTrayNodeImpl as any),
  // Trailmap L2 Drip 2 — Pass-2 cluster bounding box (paint-behind decoration).
  cluster_aabb: byKind('cluster_aabb', ClusterAabbNodeImpl as any),
  // v0.2 Council Trail peer-spine refactor (rev 2 — no withFocalDispatch
  // on peer_response; Rich variant deferred to v0.2.x).
  spine_root_marker: byKind('spine_root_marker', SpineRootMarkerNodeImpl as any),
  peer_response: byKind('peer_response', PeerResponseNodeImpl as any),
  spine_fan_chip: byKind('spine_fan_chip', SpineFanChipNodeImpl as any),
  cycle_error: byKind('cycle_error', CycleErrorNodeImpl as any),
  trail_head_marker: byKind('trail_head_marker', TrailHeadMarkerNodeImpl as any),
  squad_merge: byKind('squad_merge', SquadMergeNodeImpl as any),
  // C2.2 — host-authored merge-back convergence node.
  merge_node: byKind('merge_node', MergeNodeImpl as any),
  // Living Blueprint SP1 — summoned blueprint flip card.
  artifactory_blueprint: byKind('artifactory_blueprint', BlueprintNode as any),
  // Source Spines SP-2 — dormant stem under a collapsed card.
  spine_lane_stem: byKind('spine_lane_stem', SpineLaneStemNodeImpl as any),
  // Source Spines SP-3a — composite intent-chat conversation panel.
  spine_chat_panel: byKind('spine_chat_panel', SpineChatPanelNodeImpl as any),
};

// Back-compat alias exports for any downstream consumer that was
// importing the compact bodies by their old "just the node" name.
export {
  TurnMarkerNodeCompactImpl as TurnMarkerNode,
  LlmRoundNodeCompactImpl as LlmRoundNode,
  ToolCallDagNodeCompactImpl as ToolCallDagNode,
  PeerNodeCompactImpl as PeerNode,
  FanCollapsedChipImpl as FanCollapsedChip,
  PhaseStackGroupNodeImpl as PhaseStackGroupNode,
  CollapsedPhaseStackNodeImpl as CollapsedPhaseStackNode,
  TurnSeparatorNodeImpl as TurnSeparatorNode,
};
